import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getUserSubscriptionsByEmail } from '../services/searchUsers';
import { manualActivateSubscription } from '../services/activate_subscription';
import { getSubscriptionPackages } from '../services/subscription-packages';
import { elevateUserToAdmin } from '../services/elevateUser';
import { demoteUserToUser } from '../services/demoteUser';

const UserSearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get('email');
  
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [showActivationForm, setShowActivationForm] = useState(false);
  const [activationData, setActivationData] = useState({
    packageId: '',
    reason: ''
  });
  const [activating, setActivating] = useState(false);
  const [roleUpdating, setRoleUpdating] = useState(false);

  useEffect(() => {
    if (email) {
      fetchUserData();
    } else {
      setError('No email provided for search');
      setLoading(false);
    }
  }, [email]);

  const fetchPackages = async () => {
    try {
      setPackagesLoading(true);
      const response = await getSubscriptionPackages();
      console.log('UserSearchResults - Packages API Response:', response); // Debug log
      // Handle different response structures
      const packagesArray = response?.data || response || [];
      console.log('UserSearchResults - Packages Array:', packagesArray); // Debug log
      setPackages(Array.isArray(packagesArray) ? packagesArray : []);
    } catch (err) {
      console.error('Failed to fetch packages:', err);
      setPackages([]); // Ensure packages is always an array
    } finally {
      setPackagesLoading(false);
    }
  };

  const handleActivation = async (e) => {
    e.preventDefault();
    if (!activationData.packageId || !activationData.reason) {
      setError('Please fill in all fields');
      return;
    }
    
    setActivating(true);
    setError('');
    try {
      await manualActivateSubscription(email, parseInt(activationData.packageId), activationData.reason);
      // Refresh user data after activation
      await fetchUserData();
      setShowActivationForm(false);
      setActivationData({ packageId: '', reason: '' });
    } catch (err) {
      setError(err.message || 'Failed to activate subscription');
    } finally {
      setActivating(false);
    }
  };

  const handleOpenActivationForm = async () => {
    setShowActivationForm(true);
    // Fetch packages fresh when opening the form
    if (packages.length === 0) {
      await fetchPackages();
    }
  };

  const handleElevateUser = async () => {
    if (!userData?.id) return;
    
    if (!window.confirm(`Are you sure you want to elevate ${userData.first_name} ${userData.last_name} to admin role?`)) {
      return;
    }
    
    setRoleUpdating(true);
    setError('');
    try {
      const result = await elevateUserToAdmin(userData.id);
      setSuccess(result.message || 'User elevated to admin successfully');
      // Refresh user data to show updated role
      await fetchUserData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to elevate user');
      setTimeout(() => setError(''), 3000);
    } finally {
      setRoleUpdating(false);
    }
  };

  const handleDemoteUser = async () => {
    if (!userData?.id) return;
    
    if (!window.confirm(`Are you sure you want to demote ${userData.first_name} ${userData.last_name} to user role?`)) {
      return;
    }
    
    setRoleUpdating(true);
    setError('');
    try {
      const result = await demoteUserToUser(userData.id);
      setSuccess(result.message || 'User demoted to user successfully');
      // Refresh user data to show updated role
      await fetchUserData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to demote user');
      setTimeout(() => setError(''), 3000);
    } finally {
      setRoleUpdating(false);
    }
  };

  const fetchUserData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getUserSubscriptionsByEmail(email);
      // console.log('API Response:', response); // Debug log
      setUserData(response.data);
    } catch (err) {
      setError(err.message || 'Failed to fetch user data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'KSH 0.00';
    return `KSH ${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFeaturesList = (features) => {
    return Object.entries(features).map(([key, value]) => {
      const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      return (
        <div key={key} className="flex items-center justify-between py-2 border-b border-gray-100">
          <span className="text-sm text-gray-600">{formattedKey}</span>
          <span className={`text-sm font-medium ${
            value === true ? 'text-green-600' : 
            value === 'unlimited' ? 'text-purple-600' : 
            'text-gray-900'
          }`}>
            {value === true ? '✓ Enabled' : 
             value === 'unlimited' ? '♾️ Unlimited' : 
             value}
          </span>
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <div className="text-gray-600">Searching for user subscriptions...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg">
          <div className="flex items-center">
            <span className="text-2xl mr-3">❌</span>
            <div>
              <h3 className="font-medium">Error</h3>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate('/subscriptions')}
          className="mt-4 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
        >
          ← Back to Subscriptions
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 text-blue-600 hover:text-blue-800 flex items-center space-x-2 transition-colors"
        >
          <span>←</span>
          <span>Back</span>
        </button>
        
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 text-white shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">User Subscription Details</h1>
              <p className="text-blue-100">Complete subscription history and information</p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold">{userData.total_subscriptions}</div>
              <div className="text-blue-100">Total Subscriptions</div>
            </div>
          </div>
        </div>
      </div>

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* User Info Card */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">User Information</h2>
          <div className="flex flex-col sm:flex-row w-full sm:w-auto space-y-2 sm:space-y-0 sm:space-x-2">
            {/* Role Management Buttons */}
            {userData.role === 'user' && (
              <button
                onClick={handleElevateUser}
                disabled={roleUpdating}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors text-sm sm:text-base w-full sm:w-auto"
              >
                <span>🔝</span>
                <span className="hidden sm:inline">{roleUpdating ? 'Elevating...' : 'Elevate to Admin'}</span>
                <span className="sm:hidden">{roleUpdating ? '...' : 'Elevate'}</span>
              </button>
            )}
            {(userData.role === 'admin' || userData.role === 'super_admin') && (
              <button
                onClick={handleDemoteUser}
                disabled={roleUpdating}
                className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors text-sm sm:text-base w-full sm:w-auto"
              >
                <span>🔽</span>
                <span className="hidden sm:inline">{roleUpdating ? 'Demoting...' : 'Demote to User'}</span>
                <span className="sm:hidden">{roleUpdating ? '...' : 'Demote'}</span>
              </button>
            )}
            
            <button
              onClick={handleOpenActivationForm}
              className="bg-green-500 hover:bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors text-sm sm:text-base w-full sm:w-auto"
            >
              <span>⚡</span>
              <span className="hidden sm:inline">Activate Subscription</span>
              <span className="sm:hidden">Activate</span>
            </button>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              userData.is_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
            }`}>
              {userData.is_verified ? '✓ Verified' : '⚠ Not Verified'}
            </span>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              userData.is_locked ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
            }`}>
              {userData.is_locked ? '🔒 Locked' : '🔓 Active'}
            </span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
              {userData.first_name?.[0]}{userData.last_name?.[0]}
            </div>
            <div>
              <div className="font-semibold text-gray-900">
                {userData.first_name} {userData.last_name}
              </div>
              <div className="text-sm text-gray-500">ID: {userData.id}</div>
            </div>
          </div>
          
          <div>
            <div className="text-sm text-gray-500 mb-1">Email</div>
            <div className="font-medium text-gray-900">{userData.email}</div>
          </div>
          
          <div>
            <div className="text-sm text-gray-500 mb-1">Phone Number</div>
            <div className="font-medium text-gray-900">{userData.phone_number}</div>
          </div>
          
          <div>
            <div className="text-sm text-gray-500 mb-1">Role</div>
            <div className="font-medium text-gray-900 capitalize">{userData.role}</div>
          </div>
          
          <div>
            <div className="text-sm text-gray-500 mb-1">Active Subscriptions</div>
            <div className="font-medium text-gray-900">{userData.active_subscriptions_count}</div>
          </div>
          
          <div>
            <div className="text-sm text-gray-500 mb-1">Total Spent</div>
            <div className="font-medium text-gray-900">{formatCurrency(userData.total_spent)}</div>
          </div>
        </div>
      </div>

      {/* Activation Form */}
      {showActivationForm && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border-2 border-green-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900">Manual Subscription Activation</h3>
        </div>
        <form onSubmit={handleActivation} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Package
                <button
                  type="button"
                  onClick={fetchPackages}
                  disabled={packagesLoading}
                  className="ml-2 text-blue-600 hover:text-blue-800 text-sm disabled:text-gray-400"
                  title="Refresh packages"
                >
                  🔄 Refresh
                </button>
              </label>
              <select
                value={activationData.packageId}
                onChange={(e) => setActivationData({...activationData, packageId: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
                disabled={packagesLoading || packages.length === 0}
              >
                <option value="">
                  {packagesLoading ? 'Loading packages...' : 
                   packages.length === 0 ? 'No packages available...' : 
                   'Choose a package...'}
                </option>
                {!packagesLoading && packages && Array.isArray(packages) && packages.map((pkg) => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.package_name || pkg.name} - {pkg.duration_type} ({pkg.duration_value}) - KSH {pkg.amount || pkg.price}
                  </option>
                ))}
              </select>
              {packagesLoading && (
                <div className="flex items-center space-x-2 text-sm text-gray-500 mt-1">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
                  <span>Loading available packages...</span>
                </div>
              )}
              {!packagesLoading && packages.length === 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  No packages available. Please create packages first.
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Activation
              </label>
              <textarea
                value={activationData.reason}
                onChange={(e) => setActivationData({...activationData, reason: e.target.value})}
                placeholder="Enter reason for manual activation..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                rows="3"
                required
              />
            </div>
            
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end space-y-2 sm:space-y-0 sm:space-x-4">
              <button
                type="submit"
                disabled={activating}
                className="w-full sm:w-auto bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors text-sm sm:text-base"
              >
                {activating ? 'Activating...' : '⚡ Activate Subscription'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowActivationForm(false);
                  setActivationData({ packageId: '', reason: '' });
                }}
                className="w-full sm:w-auto bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg transition-colors text-sm sm:text-base"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Subscriptions */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Subscription History</h2>
        
        {userData.subscriptions && Array.isArray(userData.subscriptions) && userData.subscriptions.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📋</div>
            <div className="text-gray-600">No subscriptions found</div>
          </div>
        ) : userData.subscriptions && Array.isArray(userData.subscriptions) ? (
          <div className="space-y-6">
            {userData.subscriptions.map((subscription, index) => (
              <div key={subscription.subscription_id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <span className="text-2xl">📦</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{subscription.package_name}</h3>
                        <div className="text-sm text-gray-500">ID: {subscription.subscription_id}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(subscription.status)}`}>
                        {subscription.status}
                      </span>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        subscription.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {subscription.payment_status}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Duration</div>
                      <div className="font-medium text-gray-900 capitalize">{subscription.duration_type} - {subscription.duration_value}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Start Date</div>
                      <div className="font-medium text-gray-900">{formatDate(subscription.start_date)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">End Date</div>
                      <div className="font-medium text-gray-900">{formatDate(subscription.end_date)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Amount Paid</div>
                      <div className="font-medium text-gray-900">{formatCurrency(subscription.amount_paid)}</div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Features</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {getFeaturesList(subscription.features)}
                    </div>
                  </div>
                  
                  {!subscription.is_active && (
                    <div className="mt-4 text-sm text-gray-500">
                      Days remaining: {subscription.days_remaining}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📋</div>
            <div className="text-gray-600">No subscriptions data available</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSearchResults;
